import { Link, useLocation, Outlet, Navigate } from "react-router-dom";
import { Building2, ClipboardList, Download, Settings } from "lucide-react";
import { SyncIndicator } from "@/components/molecules/sync-indicator";
import { useSyncEngine } from "@/core/hooks/useSyncEngine";
import { useInactivityLogout } from "@/core/hooks/useInactivityLogout";
import { useOfflineSession } from "@/core/hooks/useOfflineSession";

const navItems = [
  { href: "/sites", label: "Sites", icon: Building2 },
  { href: "/campagnes", label: "Campagnes", icon: ClipboardList },
  { href: "/exports", label: "Exports", icon: Download },
  { href: "/reglages", label: "Réglages", icon: Settings },
] as const;

export function AppLayout() {
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading } = useOfflineSession();
  const { syncState, pendingCount, stuckCount, isStale, forceSync } = useSyncEngine();
  useInactivityLogout();

  // Auth guard
  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Pages en mode plein écran (sans header ni bottom nav)
  const isFullscreen =
    (/\/zones\/[^/]+$/.test(pathname) && !pathname.endsWith("/nouveau") && !pathname.endsWith("/modifier")) ||
    /\/pins\/[^/]+$/.test(pathname) ||
    /\/rapport\/preview$/.test(pathname);

  if (isFullscreen) {
    return <div className="bg-background"><Outlet /></div>;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 flex h-11 items-center justify-between border-b border-border bg-background px-4">
        <h1 className="text-sm font-semibold text-foreground">Ultramesure</h1>
        <SyncIndicator
          syncState={syncState}
          pendingCount={pendingCount}
          stuckCount={stuckCount}
          isStale={isStale}
          onForceSync={forceSync}
        />
      </header>

      {/* Contenu scrollable */}
      <main className="flex-1 overflow-y-auto pb-20">
        <Outlet />
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background">
        <div className="flex h-16 items-stretch">
          {navItems.map(({ href, label, icon: Icon }) => {
            const isActive = pathname.startsWith(href);
            return (
              <Link
                key={href}
                to={href}
                className={`flex flex-1 flex-col items-center justify-center gap-1 text-xs transition-colors ${
                  isActive
                    ? "text-primary font-medium"
                    : "text-muted-foreground"
                }`}
              >
                <Icon className="size-5" />
                <span>{label}</span>
              </Link>
            );
          })}
        </div>
        {/* Safe area pour les téléphones avec barre gestuelle */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </nav>
    </div>
  );
}
